import { useMemo, useState } from 'react';
import {
  Badge, Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text,
} from '@tremor/react';
import { t } from '../lib/i18n';
import { usePagination } from '../lib/usePagination';
import Pagination from './Pagination';
import type { VendorScorecard } from '../api/types';

const fmtMxn = new Intl.NumberFormat('es-MX', {
  style: 'currency', currency: 'MXN', minimumFractionDigits: 0, maximumFractionDigits: 0,
});
const fmtPct = new Intl.NumberFormat('es-MX', {
  style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1,
});

interface Props {
  rows: VendorScorecard[];
  showCost: boolean;
  onSelect: (v: VendorScorecard) => void;
}

type SortKey = 'total_ordered' | 'fill_rate' | 'lead_time' | 'cost_trend' | 'po_count';

export default function VendorScorecardTable({ rows, showCost, onSelect }: Props) {
  const [sortBy, setSortBy] = useState<SortKey>('total_ordered');

  const sorted = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      switch (sortBy) {
        case 'total_ordered': return (b.total_ordered_mxn ?? 0) - (a.total_ordered_mxn ?? 0);
        case 'fill_rate':     return (b.fill_rate ?? -Infinity) - (a.fill_rate ?? -Infinity);
        case 'lead_time':     return (a.avg_lead_time_days ?? Infinity) - (b.avg_lead_time_days ?? Infinity);
        case 'cost_trend':    return (b.cost_trend_pct ?? -Infinity) - (a.cost_trend_pct ?? -Infinity);
        case 'po_count':      return b.po_count - a.po_count;
      }
    });
    return arr;
  }, [rows, sortBy]);

  const pg = usePagination(sorted);

  if (rows.length === 0) {
    return <Card><Text>{t.vendors.emptyVendors}</Text></Card>;
  }

  const headerBtn = (label: string, key: SortKey) => (
    <button
      type="button"
      onClick={() => setSortBy(key)}
      className={'hover:underline ' + (sortBy === key ? 'text-tremor-brand font-semibold' : '')}
    >
      {label}
    </button>
  );

  const fillBadge = (fr: number | null) => {
    if (fr == null) return <span className="text-tremor-content">—</span>;
    const color = fr >= 0.95 ? 'green' : fr >= 0.80 ? 'amber' : 'red';
    return <Badge color={color}>{fmtPct.format(fr)}</Badge>;
  };

  return (
    <Card className="p-0 overflow-hidden">
      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell>{t.vendors.cols.vendor}</TableHeaderCell>
            <TableHeaderCell className="text-right">{headerBtn(t.vendors.cols.poCount, 'po_count')}</TableHeaderCell>
            {showCost && (
              <TableHeaderCell className="text-right">{headerBtn(t.vendors.cols.ordered, 'total_ordered')}</TableHeaderCell>
            )}
            {showCost && (
              <TableHeaderCell className="text-right">{t.vendors.cols.received}</TableHeaderCell>
            )}
            <TableHeaderCell className="text-right">{headerBtn(t.vendors.cols.fillRate, 'fill_rate')}</TableHeaderCell>
            <TableHeaderCell className="text-right">{headerBtn(t.vendors.cols.leadTime, 'lead_time')}</TableHeaderCell>
            {showCost && (
              <TableHeaderCell className="text-right">{headerBtn(t.vendors.cols.costTrend, 'cost_trend')}</TableHeaderCell>
            )}
            <TableHeaderCell className="text-right">{t.vendors.cols.lineCount}</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pg.pageRows.map((v) => (
            <TableRow
              key={`${v.sid}-${v.sup_id}`}
              className="cursor-pointer hover:bg-tremor-background-muted"
              onClick={() => onSelect(v)}
            >
              <TableCell>
                <span className="font-mono text-xs text-tremor-content mr-2">{v.sup_id}</span>
                {v.name}
              </TableCell>
              <TableCell className="text-right">{v.po_count}</TableCell>
              {showCost && (
                <TableCell className="text-right">{v.total_ordered_mxn !== undefined ? fmtMxn.format(v.total_ordered_mxn) : '—'}</TableCell>
              )}
              {showCost && (
                <TableCell className="text-right">{v.total_received_mxn !== undefined ? fmtMxn.format(v.total_received_mxn) : '—'}</TableCell>
              )}
              <TableCell className="text-right">{fillBadge(v.fill_rate)}</TableCell>
              <TableCell className="text-right">{v.avg_lead_time_days == null ? '—' : v.avg_lead_time_days.toFixed(1)}</TableCell>
              {showCost && (
                <TableCell className="text-right">
                  {v.cost_trend_pct == null ? '—' : (
                    <span className={v.cost_trend_pct > 0 ? 'text-red-600 dark:text-red-400' : v.cost_trend_pct < 0 ? 'text-green-600 dark:text-green-400' : ''}>
                      {fmtPct.format(v.cost_trend_pct)}
                    </span>
                  )}
                </TableCell>
              )}
              <TableCell className="text-right">{v.line_count}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Pagination {...pg} className="px-4" />
    </Card>
  );
}
